import './request-info.scss';
import {useIntl} from "react-intl";
import {Field, useFormikContext} from 'formik';
import {typeIdentification} from '../../../__mocks__/data';
import _ from 'lodash';
import {WLOCreditAPIService} from '../../../services';
import toast from "react-hot-toast";
import {useLoginContext} from '../../../hooks/useLoginContext';
import {useRequestContext} from '../../../hooks/useRequestContext';
import {ANIPersona, CygnusPersona} from '../../../context/RequestContext';
import {useEffect, useRef, useState} from 'react';
import ClockLoader from "react-spinners/ClockLoader";

interface FormKnowYouProps {
    errors?: any,
    values?: any,
}

export function FormKnowYou(props: FormKnowYouProps) {
    const {user} = useLoginContext();
    const {request, setRequest} = useRequestContext();
    const [isLoadingInfo, setIsLoadingInfo] = useState(false);
    const [identityFieldsLocked, setIdentityFieldsLocked] = useState(false);
    const {errors, values} = props;
    const {setFieldValue, handleChange} = useFormikContext();
    const searchIdRef = useRef(0);
    const intl = useIntl();
    const isAutogestion = user?.istipo === '2' || String(user?.idProfile) === '2';

    const clearIdentityData = () => {
        setFieldValue('Pws_Nombres', '');
        setFieldValue('Pws_Apellidos1', '');
        setFieldValue('Pws_Apellidos2', '');
        setFieldValue('Pws_i_codigo', '');
        if (!isAutogestion) {
            setFieldValue('Pws_Fec_expe', '');
        }
        setIdentityFieldsLocked(false);
        setRequest({
            ...request,
            personCygnus: undefined,
            personANI: undefined,
            searchANI: false,
        });
        localStorage.removeItem('person');
        localStorage.removeItem('estadoCedula');
    };

    const isSearchCurrent = (searchId: number) => searchId === searchIdRef.current;

    const handleIdentificationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        clearIdentityData();
        handleChange(e);
    };

    const handleSearch = async (override?: { tipo?: string; ident?: string }) => {
        const tipoDoc = override?.tipo || values.Pws_Tip_Identificacion;
        const ident = override?.ident || values.Pws_Identificacion;

        if (!_.isEmpty(tipoDoc) || !_.isEmpty(ident)) {
            if (!ident || String(ident).length < 5) {
                toast.error('Debe indicar un Número de Identificación entre 5 y 10 caracteres', {
                    duration: 5000,
                });
                return;
            }
            if (isNaN(Number(ident))) {
                toast.error("El documento de identificación debe ser numérico", {
                    duration: 5000,
                });
                return;
            }
        } else {
            return;
        }

        const searchId = ++searchIdRef.current;
        if (!isAutogestion) {
            clearIdentityData();
        }
        setIsLoadingInfo(true);

        try {
            const person = await WLOCreditAPIService.consultaSolicitudCygnus({
                Pws_Tip_Identificacion: tipoDoc!,
                Pws_Identificacion: ident!
            });

            if (!isSearchCurrent(searchId)) return;

            if (!_.isEmpty(person.payload)) {
                const pCygnus = WLOCreditAPIService.extraerPersonaCygnus(person) as CygnusPersona | undefined;
                if (WLOCreditAPIService.consultaCygnusTienePersona(person) && pCygnus) {
                    setFieldValue('Pws_Apellidos1', pCygnus.Pws_Apellidos1);
                    setFieldValue('Pws_Apellidos2', pCygnus.Pws_Apellidos2);
                    setFieldValue('Pws_Nombres', pCygnus.Pws_Nombres);
                    if (pCygnus.Pws_i_codigo) {
                        setFieldValue('Pws_i_codigo', pCygnus.Pws_i_codigo);
                    }
                    if (pCygnus.Pws_Fec_expe) {
                        setFieldValue('Pws_Fec_expe', WLOCreditAPIService.fechaCygnusToFormIso(pCygnus.Pws_Fec_expe));
                    }
                    if (pCygnus.Pws_Fec_nacime) {
                        setFieldValue('Pws_Fec_nacime', WLOCreditAPIService.fechaCygnusToFormIso(pCygnus.Pws_Fec_nacime));
                    }
                    setRequest({...request, personCygnus: pCygnus, searchANI: false, errorConsulta: false});
                    localStorage.setItem('person', JSON.stringify(pCygnus));
                    setIdentityFieldsLocked(true);
                    setIsLoadingInfo(false);
                } else {
                    try {
                        const result = await WLOCreditAPIService.consultaSolicitudANI({
                            Pws_Tip_Identificacion: tipoDoc!,
                            Pws_Identificacion: ident!
                        }, true);

                        if (!isSearchCurrent(searchId)) return;

                        const pANI = result.persona as ANIPersona;
                        const nombres = [pANI.primerNombre, pANI.segundoNombre].filter(Boolean).join(' ');
                        setFieldValue('Pws_Apellidos1', pANI.primerApellido);
                        setFieldValue('Pws_Apellidos2', pANI.segundoApellido);
                        setFieldValue('Pws_Nombres', nombres);
                        setFieldValue('Pws_Fec_expe', WLOCreditAPIService.fechaAniToFormIso(pANI.fechaExpedicion));
                        setRequest({...request, personANI: pANI, searchANI: true, errorConsulta: false});
                        localStorage.setItem('person', JSON.stringify(result.persona));
                        setIdentityFieldsLocked(true);
                        setIsLoadingInfo(false);
                    } catch (e) {
                        if (!isSearchCurrent(searchId)) return;
                        setRequest({...request, searchANI: true, errorConsulta: false});
                        setIdentityFieldsLocked(false);
                        setIsLoadingInfo(false);
                    }
                }
            } else {
                toast.error('En estos momentos no podemos atender su solicitud', {
                    duration: 5000,
                });
                setIsLoadingInfo(false);
            }
        } catch (e) {
            if (!isSearchCurrent(searchId)) return;
            const error = e as any;
            if (!_.isEmpty(error.response?.data)) {
                setRequest({...request, errorConsulta: true});
                toast.error("El documento de identificación no existe.", {
                    duration: 5000,
                });
            } else {
                setRequest({...request, errorConsulta: true});
                toast.error('En estos momentos no podemos atender su solicitud', {
                    duration: 5000,
                });
            }
            setIsLoadingInfo(false);
        }
    };

    useEffect(() => {
        if (!isAutogestion) return;
        const datosPersonaString = localStorage.getItem("personaValidada");
        if (!datosPersonaString) return;
        const datosPersona = JSON.parse(datosPersonaString);
        if (datosPersona.numIdenVal) {
            setFieldValue('Pws_Identificacion', datosPersona.numIdenVal);
        }
        if (datosPersona.tipIdenVal) {
            setFieldValue('Pws_Tip_Identificacion', datosPersona.tipIdenVal);
        }
        if (datosPersona.fecExpeVal) {
            setFieldValue('Pws_Fec_expe', datosPersona.fecExpeVal);
        }
        handleSearch({
            tipo: datosPersona.tipIdenVal,
            ident: datosPersona.numIdenVal,
        });
        // Solo al montar el paso de autogestión
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className={'row'}>
            <ClockLoader id='spinner' color={"#01A451"} loading={isLoadingInfo} size={100}/>
            <div className={"title-medium-red"}>{intl.formatMessage({id: "form_credit_information_title"})}</div>
            <div className={"subtitle-medium"}>{intl.formatMessage({id: "form_credit_information_subtitle"})}</div>
            <div className={'flex-container-form-know-you'}>
                <div className={"flex-container-form-know-you-item-right flex-grow-6"}>
                    <Field as="select"
                        id={"Pws_Tip_Identificacion"}
                        name={"Pws_Tip_Identificacion"}
                        disabled={isAutogestion}
                        className={`${errors.Pws_Tip_Identificacion ? 'form-know-you-item-input-small input-error' : 'form-know-you-item-input-small'}`}>
                        <option>{intl.formatMessage({id: "placeholder_identification_type"})}</option>
                        {typeIdentification && typeIdentification.WS_TIPOS_IDENTIFICACION_IResult.LstTiposIdent.Datos_TiposIdentificacion.map((item, key: number) => {
                            return (
                                <option key={key} value={item.s_codigo}>{item.s_descripcion}</option>
                            )
                        })}
                    </Field>
                </div>
                <div className={"flex-container-form-know-you-item-left flex-grow-6"}>
                    <Field id={"Pws_Identificacion"}
                        name={"Pws_Identificacion"}
                        disabled={isAutogestion}
                        onChange={isAutogestion ? handleChange : handleIdentificationChange}
                        onBlur={isAutogestion ? undefined : handleSearch}
                        className={`${errors.Pws_Identificacion ? 'form-know-you-item-input-small input-error' : 'form-know-you-item-input-small'}`}
                        placeholder={intl.formatMessage({id: "placeholder_document_number"})}/>
                </div>
            </div>
            <div className={'flex-container-form-know-you'}>
                <div className={"flex-container-form-know-you-item-center flex-grow-6"}>
                    <Field id={"Pws_Nombres"}
                        name={"Pws_Nombres"}
                        disabled={isLoadingInfo || identityFieldsLocked}
                        className={`${errors.Pws_Nombres ? 'form-know-you-item-input-large input-error' : 'form-know-you-item-input-large'}`}
                        placeholder={"Nombres"}/>
                </div>
            </div>
                <div className={'flex-container-form-know-you'}>
                <div className={"flex-container-form-know-you-item-right flex-grow-6"}>
                    <Field id={"Pws_Apellidos1"}
                        name={"Pws_Apellidos1"}
                        disabled={isLoadingInfo || identityFieldsLocked}
                        className={`${errors.Pws_Apellidos1 ? 'form-know-you-item-input-small input-error' : 'form-know-you-item-input-small'}`}
                        placeholder={'Primer Apellido'}/>
                </div>
                <div className={"flex-container-form-know-you-item-left flex-grow-6"}>
                    <Field id={"Pws_Apellidos2"}
                        name={"Pws_Apellidos2"}
                        disabled={isLoadingInfo || identityFieldsLocked}
                        className={`${errors.Pws_Apellidos2 ? 'form-know-you-item-input-small input-error' : 'form-know-you-item-input-small'}`}
                        placeholder={'Segundo Apellido'}/>
                </div>
                </div>
            <div className={'flex-container-form-know-you'}>
                <div className={"flex-container-form-know-you-item-right flex-grow-6"}>
                    <Field id={"Pws_Telefono1"}
                        name={"Pws_Telefono1"}
                        className={`${errors.Pws_Telefono1 ? 'form-know-you-item-input-small input-error' : 'form-know-you-item-input-small'}`}
                        placeholder={intl.formatMessage({id: "placeholder_phone_1"})}/>
                </div>
                <div className={"flex-container-form-know-you-item-left flex-grow-6"}>
                    <Field id={"Pws_Correo"}
                        name={"Pws_Correo"}
                        className={`${errors.Pws_Correo ? 'form-know-you-item-input-small input-error' : 'form-know-you-item-input-small'}`}
                        placeholder={intl.formatMessage({id: "placeholder_mail"})}/>
                </div>
            </div>
            <div className={'flex-container-form-know-you'}>
                <div className={"flex-container-form-know-you-item-right flex-grow-6"}>
                    <Field id={"Pws_Telefono2"}
                        name={"Pws_Telefono2"}
                        className={`${errors.Pws_Telefono2 ? 'form-know-you-item-input-small input-error' : 'form-know-you-item-input-small'}`}
                        placeholder={intl.formatMessage({id: "placeholder_phone_2"})}/>
                </div>
                <div className={"flex-container-form-know-you-item-left flex-grow-6"}>
                    <Field id={"mail_confirmation"}
                    onPaste={(e:any)=>{
                        e.preventDefault()
                        return false;
                    }} 
                    autoComplete="off"
                    name={"mail_confirmation"}
                    className={`${errors.mail_confirmation ? 'form-know-you-item-input-small input-error' : 'form-know-you-item-input-small'}`}
                    placeholder={intl.formatMessage({id: "placeholder_mail_confirmation"})}/>
                </div>
            </div>
        </div>

    )
}
